import { CsvRawRow, DisplayRow, UploadResult } from "./types";

const templateCsv = (includeInternalUserId: boolean) =>
  (includeInternalUserId
    ? [
        "first_name,last_name,email,badge_number,rank_short,contact_phone_number,internal_user_id",
        "John,Doe,john.doe@example.gov,12345,Sgt,+1 555 123 4567,E1001",
        "Jane,Smith,jane.smith@example.gov,67890,Cpl,+1 555 765 4321,E1002",
      ]
    : [
        "first_name,last_name,email,badge_number,rank_short,contact_phone_number",
        "John,Doe,john.doe@example.gov,12345,Sgt,+1 555 123 4567",
        "Jane,Smith,jane.smith@example.gov,67890,Cpl,+1 555 765 4321",
      ]
  ).join("\n");

export const downloadTemplate = (includeInternalUserId = false) => {
  const blob = new Blob([templateCsv(includeInternalUserId)], {
    type: "text/csv",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "users_template.csv";
  a.click();
  URL.revokeObjectURL(url);
};

export const extractErrorMsg = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (error !== null && typeof error === "object") {
    const body = error as Record<string, any>;
    return (
      body?.detail?.msg ??
      (typeof body?.detail === "string" ? body.detail : null) ??
      JSON.stringify(error)
    );
  }
  return String(error);
};

// Existing users are only granted permissions, so the Internal User ID is
// required of rows that will create a user.
export const validateRow = (
  row: Partial<DisplayRow>,
  requireInternalUserId = false,
): string[] => {
  const errors: string[] = [];
  const email = row.email?.toLowerCase().trim() ?? "";
  if (!email) errors.push("Email is required");
  else if (!email.includes("@")) errors.push("Email must be valid");
  if (requireInternalUserId && !row.isExisting && !row.internal_user_id?.trim())
    errors.push("Internal User ID is required");
  return errors;
};

export const recheckDuplicates = (
  rows: DisplayRow[],
  existingEmails?: Set<string>,
  requireInternalUserId = false,
): DisplayRow[] => {
  const firstSeen = new Map<string, number>();
  return rows.map((row) => {
    const email = row.email.toLowerCase().trim();
    const isExisting = !!email && !!existingEmails?.has(email);
    const fieldErrors = validateRow(
      { ...row, isExisting },
      requireInternalUserId,
    );
    if (email && !isExisting) {
      if (firstSeen.has(email)) {
        fieldErrors.push("Duplicate email in CSV");
      } else {
        firstSeen.set(email, row.id);
      }
    }
    return { ...row, errors: fieldErrors, isExisting };
  });
};

export const processRows = (
  raw: CsvRawRow[],
  requireInternalUserId = false,
): { rows: DisplayRow[]; duplicatesRemoved: number } => {
  const seen = new Set<string>();
  const deduped: CsvRawRow[] = [];
  let duplicatesRemoved = 0;

  for (const row of raw) {
    const normalizedEmail = (row.email ?? "").toLowerCase().trim();
    const normalizedRow = { ...row, email: normalizedEmail };
    if (normalizedEmail && seen.has(normalizedEmail)) {
      duplicatesRemoved++;
    } else {
      if (normalizedEmail) seen.add(normalizedEmail);
      deduped.push(normalizedRow);
    }
  }

  const rows: DisplayRow[] = deduped.map((row, i) => ({
    id: i,
    index: i + 1,
    first_name: row.first_name?.trim() ?? "",
    last_name: row.last_name?.trim() ?? "",
    email: row.email?.trim() ?? "",
    badge_number: row.badge_number?.trim() ?? "",
    rank_short: row.rank_short?.trim() ?? "",
    contact_phone_number: row.contact_phone_number?.trim() ?? "",
    internal_user_id: row.internal_user_id?.trim() ?? "",
    errors: validateRow(row, requireInternalUserId),
    isExisting: false,
    permissions: [],
  }));

  return { rows, duplicatesRemoved };
};

export const downloadFailedCSV = (results: UploadResult[]) => {
  const failed = results.filter((r) => r.error);
  const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const lines = [
    "first_name,last_name,email,badge_number,rank_short,contact_phone_number,internal_user_id,error",
    ...failed.map((r) =>
      [
        r.row.first_name,
        r.row.last_name,
        r.row.email,
        r.row.badge_number,
        r.row.rank_short,
        r.row.contact_phone_number,
        r.row.internal_user_id,
        r.error ?? "",
      ]
        .map(escape)
        .join(","),
    ),
  ].join("\n");
  const blob = new Blob([lines], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "failed_users.csv";
  a.click();
  URL.revokeObjectURL(url);
};
